import { groupBy, keyBy, sortBy, sumBy, uniq } from "lodash";

import { DateHelper, msPerDay } from "../../../../common/src";
import { numberFormat, type FormatNumericInput } from "../../../../common/src/utils/FormatFunctions";
import { FONAV_CARRY_DAY_COUNT_BASIS, FoNavCdsIndex, FoNavRiskType } from "./constants";
import { CalendarStateEnum, InstrumentModelLegTypesEnum } from "../../types.generated";

export interface FoNavGridRow {
    name: string;
    startValue: string;
    endValue: string;
    change: string;
    instrumentId?: string;
    isin?: string;
}

export interface FoNavBondGridRow extends FoNavGridRow {
    timeToMaturityYears: number | null;
    yieldStartValue: string;
    yieldEndValue: string;
    yieldChange: string;
    lastPriceDate: string;
}

export interface FoNavRow {
    name: string;
    startValue: number | null;
    endValue: number | null;
    changeBp: number | null;
    instrumentId?: string;
    isin?: string;
    maturityPeriod?: string;
    timeToMaturityYears?: number | null;
}

const FIXED_LEG_TYPES = new Set<InstrumentModelLegTypesEnum>([
    InstrumentModelLegTypesEnum.FixedRateLeg,
    InstrumentModelLegTypesEnum.GenericFixedRateLeg
]);

type TimeSeriesLike = {
    items?: { date: string; value: number }[] | null;
    dates?: string[] | null;
    values?: string[] | null;
} | null | undefined;

export function getFixedLegMaturityPeriod(
    legs: { _t?: InstrumentModelLegTypesEnum | null; maturityPeriod?: string | null }[] | null | undefined
): string | null {
    if (!legs || legs.length === 0) {
        return null;
    }

    const fixedLeg = legs.find((leg) => leg._t && FIXED_LEG_TYPES.has(leg._t));
    return fixedLeg?.maturityPeriod ?? null;
}

export function maturityPeriodToYears(period: string): number | null {
    if (!period) {
        return null;
    }

    const match = period.trim().toUpperCase().match(/^(\d+(?:\.\d+)?)([DWMY])$/);
    if (!match) {
        return null;
    }

    const value = parseFloat(match[1]);
    const unit = match[2];

    if (unit === "D") {
        return value / 365;
    }
    if (unit === "W") {
        return value / 52;
    }
    if (unit === "M") {
        return value / 12;
    }
    if (unit === "Y") {
        return value;
    }

    return null;
}

export function getTimeSeriesValueAtDate(timeSeries: TimeSeriesLike | TimeSeriesLike[], date: string): number | null {
    const seriesList = Array.isArray(timeSeries) ? timeSeries : [timeSeries];
    const series = seriesList.find((item) => item);
    if (!series) {
        return null;
    }

    if (series.items && series.items.length > 0) {
        const item = series.items.find((entry) => entry.date === date);
        return item && typeof item.value === "number" ? item.value : null;
    }

    if (series.dates && series.values) {
        const index = series.dates.indexOf(date);
        if (index >= 0) {
            const parsedValue = Number(series.values[index]);
            return Number.isFinite(parsedValue) ? parsedValue : null;
        }
    }

    return null;
}

export interface FoNavPositionRow {
    modelType?: string | null;
    isCashAccount?: boolean | null;
    instrument?: { modelType?: string | null } | null;
    swaplab2Results?: {
        creditYieldDelta?: number | null;
        interestRateYieldDelta?: number | null;
    } | null;
}

export interface FoNavModelTypeSummaryRow {
    modelType: string;
    positionCount: number;
    creditYieldDelta: number;
    interestRateYieldDelta: number;
    creditDeltaPerNav: number | null;
    yieldDeltaPerNav: number | null;
}

export function getPositionModelType(row: FoNavPositionRow): string {
    if (row.isCashAccount || row.modelType === "Balance" || row.instrument?.modelType === "Balance") {
        return "Cash";
    }
    return row.modelType || row.instrument?.modelType || "Other";
}

export function groupPositionsByModelType(positions: FoNavPositionRow[] | null | undefined): FoNavModelTypeSummaryRow[] {
    if (!positions || positions.length === 0) {
        return [];
    }

    const grouped = groupBy(positions, getPositionModelType);
    const rows = Object.entries(grouped).map(([modelType, items]) => ({
        modelType,
        positionCount: items.length,
        creditYieldDelta: sumBy(items, (position) => position.swaplab2Results?.creditYieldDelta ?? 0),
        interestRateYieldDelta: sumBy(items, (position) => position.swaplab2Results?.interestRateYieldDelta ?? 0),
        creditDeltaPerNav: null,
        yieldDeltaPerNav: null
    }));

    return sortBy(rows, "modelType");
}

export function deltaPerNav(delta: number, nav: number | null): number | null {
    if (nav === null || nav === 0) {
        return null;
    }
    return (delta * 1e4) / nav;
}

export function withDeltaPerNav(rows: FoNavModelTypeSummaryRow[], nav: number | null): FoNavModelTypeSummaryRow[] {
    return rows.map((row) => ({
        ...row,
        creditDeltaPerNav: deltaPerNav(row.creditYieldDelta, nav),
        yieldDeltaPerNav: deltaPerNav(row.interestRateYieldDelta, nav)
    }));
}

export function formatFoNavDurationInput(value: number | null): string {
    if (value === null || typeof value === "undefined" || !Number.isFinite(value)) {
        return "";
    }
    return value.toFixed(2);
}

export function formatFoNavChangeInput(value: number | null): string {
    if (value === null || typeof value === "undefined" || !Number.isFinite(value)) {
        return "";
    }
    return value.toFixed(1);
}

export function formatFoNavWeightInput(weight: number | null): string {
    if (weight === null || typeof weight === "undefined" || !Number.isFinite(weight)) {
        return "";
    }
    return (weight * 100).toFixed(1);
}

export type FoNavRiskFactorGroup = "Swaption" | "Other" | "Bond" | "Swap" | "CDS";

export interface FoNavRiskFactorDefinition {
    id: string;
    group: FoNavRiskFactorGroup;
    label: string;
    weight: number;
    totalRisk: number | null;
    instrumentId?: string | null;
}

export interface FoNavRiskFactorDefaults {
    id: string;
    duration: number | null;
    change: number | null;
    weight: number | null;
}

export interface FoNavBondRiskInput {
    instrumentId?: string | null;
    isin?: string | null;
    name: string;
    maturityDate?: string | null;
    spreadStart: unknown;
    spreadEnd: unknown;
    yieldStart: unknown;
    yieldEnd: unknown;
}

export interface FoNavSwapRiskInput {
    instrumentId?: string | null;
    name: string;
    maturityPeriod?: string | null;
    timeToMaturityYears: number | null;
    startRate: unknown;
    endRate: unknown;
}

export interface FoNavCdsRiskInput {
    instrumentId?: string | null;
    /** Index key, used to look up weights. */
    name: string;
    /** Name of the series instrument actually used. */
    seriesName?: string | null;
    /** Newer series that is only priced on the end date, i.e. the index rolled within the period. */
    pendingSeriesName?: string | null;
    startPrice: unknown;
    endPrice: unknown;
}

/**
 * Index of the half year roll period a date falls in. CDS indices roll to a new series on 20 March and 20 September.
 */
function getFoNavCdsRollPeriod(date: string): number {
    const year = parseInt(date.slice(0, 4), 10);
    const monthDay = date.slice(5, 10);
    let period = year * 2 - 1;
    if (monthDay >= "09-20") {
        period = year * 2 + 1;
    } else if (monthDay >= "03-20") {
        period = year * 2;
    }
    return period;
}

/**
 * The series expected to be on the run at `date`, counted in rolls from the index's anchor series.
 */
export function getFoNavCdsExpectedSeries(index: FoNavCdsIndex, date: string): number {
    return index.anchorSeries + getFoNavCdsRollPeriod(date) - getFoNavCdsRollPeriod(index.anchorRollDate);
}

/**
 * Candidate instrument names around the expected series, ordered from oldest to newest series. Within a series the
 * templates keep their configured order, so a later template (the newer naming convention) wins when both exist.
 */
export function getFoNavCdsSeriesCandidates(index: FoNavCdsIndex, dates: string[]): string[] {
    const expected = dates.filter(Boolean).map((date) => getFoNavCdsExpectedSeries(index, date));
    if (expected.length === 0) {
        return [];
    }
    const candidates: string[] = [];
    for (let series = Math.min(...expected) - 1; series <= Math.max(...expected) + 1; series++) {
        index.nameTemplates.forEach((template) => candidates.push(template.replace("{series}", String(series))));
    }
    return uniq(candidates);
}

/**
 * Picks, per index, the newest series that has a price on both the start and the end date. On a roll date the new
 * series only has an end price, so the previous series is kept and the new one is reported as pending.
 */
export function resolveFoNavCdsSeries(
    indices: FoNavCdsIndex[],
    dates: string[],
    instruments: { _id: string; name: string }[],
    startPriceByInstrumentId: Record<string, number | null>,
    endPriceByInstrumentId: Record<string, number | null>
): FoNavCdsRiskInput[] {
    const instrumentsByName = keyBy(instruments, (instrument) => instrument.name);
    const hasPrice = (value: number | null | undefined): boolean => typeof value === "number" && Number.isFinite(value);

    return indices.map((index) => {
        const candidates = getFoNavCdsSeriesCandidates(index, dates)
            .map((candidateName) => instrumentsByName[candidateName])
            .filter((instrument) => typeof instrument !== "undefined")
            .reverse();
        const pricedIndex = candidates.findIndex(
            (instrument) => hasPrice(startPriceByInstrumentId[instrument._id]) && hasPrice(endPriceByInstrumentId[instrument._id])
        );
        const instrument = pricedIndex === -1 ? candidates[0] : candidates[pricedIndex];
        const pending = candidates
            .slice(0, pricedIndex === -1 ? 0 : pricedIndex)
            .find((candidate) => hasPrice(endPriceByInstrumentId[candidate._id]));

        return {
            instrumentId: instrument?._id ?? null,
            name: index.key,
            seriesName: instrument?.name ?? null,
            pendingSeriesName: pending?.name ?? null,
            startPrice: instrument ? (startPriceByInstrumentId[instrument._id] ?? null) : null,
            endPrice: instrument ? (endPriceByInstrumentId[instrument._id] ?? null) : null
        };
    });
}

export function calculateWeightedRiskImpact(
    totalRisk: number | null,
    weight: number | null,
    changeBp: number | null
): number | null {
    if (totalRisk === null || weight === null || changeBp === null) {
        return null;
    }
    return calculateRiskImpact(totalRisk * weight, changeBp);
}

export function getFoNavBondWeight(name: string, bondWeights: Record<string, number>): number | null {
    const normalized = name.replace(/\s+/g, "").toUpperCase();
    const match = Object.entries(bondWeights).find(([bondName]) => {
        return normalized.includes(bondName.replace(/\s+/g, "").toUpperCase());
    });
    if (!match) {
        return null;
    }
    return match[1];
}

export function buildFoNavBondRiskFactorDefaults(
    bonds: FoNavBondRiskInput[],
    bondTotalRisk: number | null,
    bondWeights: Record<string, number>
): FoNavRiskFactorDefaults[] {
    return bonds
        .map((bond) => {
            const weight = getFoNavBondWeight(bond.name, bondWeights);
            if (weight === null) {
                return null;
            }
            return {
                id: `bond-${bond.isin ?? bond.name}`,
                duration: bondTotalRisk,
                change: computeYieldPercentChangeBp(bond.yieldStart, bond.yieldEnd),
                weight
            };
        })
        .filter((row): row is FoNavRiskFactorDefaults => row !== null);
}

export function buildFoNavSwapRiskFactorDefaults(
    swaps: FoNavSwapRiskInput[],
    swapTenorWeights: Record<number, number>,
    swapTotalRisk: number | null
): FoNavRiskFactorDefaults[] {
    return sortBy(
        swaps
            .map((swap) => {
                const tenor =
                    swap.timeToMaturityYears === null ? null : Math.round(swap.timeToMaturityYears);
                const weight = tenor === null ? null : (swapTenorWeights[tenor] ?? null);
                if (weight === null || tenor === null) {
                    return null;
                }

                return {
                    id: `swap-${tenor}y`,
                    tenor,
                    duration: swapTotalRisk,
                    change: computeChangeBp(swap.startRate, swap.endRate),
                    weight
                };
            })
            .filter((row): row is FoNavRiskFactorDefaults & { tenor: number } => row !== null),
        "tenor"
    ).map(({ tenor: _tenor, ...row }) => row);
}

export function buildFoNavCdsRiskFactorDefaults(
    cdsRows: FoNavCdsRiskInput[],
    cdsWeights: Record<string, number>,
    cdsTotalRisk: number | null
): FoNavRiskFactorDefaults[] {
    return cdsRows
        .map((row) => {
            const weight = cdsWeights[row.name] ?? null;
            if (weight === null || weight === 0) {
                return null;
            }
            return {
                id: `cds-${row.name}`,
                duration: cdsTotalRisk,
                change: computeChangeBp(row.startPrice, row.endPrice),
                weight
            };
        })
        .filter((row): row is FoNavRiskFactorDefaults => row !== null);
}

export function formatFoNavCdsLabel(name: string): string {
    return name.replace(/\s+CDSI\s+S\d+/i, "");
}

export function buildFoNavBondRiskFactorDefinitions(
    bonds: FoNavBondRiskInput[],
    asOfDate: string,
    bondWeights: Record<string, number>
): FoNavRiskFactorDefinition[] {
    return bonds
        .map((bond): FoNavRiskFactorDefinition | null => {
            const weight = getFoNavBondWeight(bond.name, bondWeights);
            if (weight === null) {
                return null;
            }

            const years = getTimeToMaturityYears(bond.maturityDate, asOfDate);
            let label = bond.name;
            if (years !== null) {
                label = `${bond.name} (${years.toFixed(1)}y)`;
            }

            return {
                id: `bond-${bond.isin ?? bond.name}`,
                group: "Bond" as const,
                label,
                weight,
                totalRisk: null,
                instrumentId: bond.instrumentId ?? null
            };
        })
        .filter((row): row is FoNavRiskFactorDefinition => row !== null);
}

export function buildFoNavSwapRiskFactorDefinitions(
    swaps: FoNavSwapRiskInput[],
    swapTenorWeights: Record<number, number>,
    swapTotalRisk: number | null
): FoNavRiskFactorDefinition[] {
    return sortBy(
        swaps
            .map((swap): (FoNavRiskFactorDefinition & { tenor: number }) | null => {
                const tenor =
                    swap.timeToMaturityYears === null ? null : Math.round(swap.timeToMaturityYears);
                const weight = tenor === null ? null : (swapTenorWeights[tenor] ?? null);
                if (weight === null || tenor === null) {
                    return null;
                }

                return {
                    id: `swap-${tenor}y`,
                    tenor,
                    group: "Swap" as const,
                    label: `${tenor}Y`,
                    weight,
                    totalRisk: swapTotalRisk,
                    instrumentId: swap.instrumentId ?? null
                };
            })
            .filter((row): row is FoNavRiskFactorDefinition & { tenor: number } => row !== null),
        "tenor"
    ).map(({ tenor: _tenor, ...row }) => row);
}

export function buildFoNavCdsRiskFactorDefinitions(
    cdsRows: FoNavCdsRiskInput[],
    cdsWeights: Record<string, number>,
    cdsTotalRisk: number | null
): FoNavRiskFactorDefinition[] {
    return cdsRows
        .map((row): FoNavRiskFactorDefinition | null => {
            const weight = cdsWeights[row.name] ?? 0;
            if (weight === 0) {
                return null;
            }
            return {
                id: `cds-${row.name}`,
                group: "CDS" as const,
                label: formatFoNavCdsLabel(row.name),
                weight,
                totalRisk: cdsTotalRisk,
                instrumentId: row.instrumentId ?? null
            };
        })
        .filter((row): row is FoNavRiskFactorDefinition => row !== null);
}

export function buildFoNavEstimateDurations(
    positions: FoNavPositionRow[] | null | undefined,
    totalNav: number | null
): Record<FoNavRiskType, number | null> {
    const riskRows = groupPositionsByModelType(positions);
    const rowsWithPerNav = withDeltaPerNav(riskRows, totalNav);
    const byModelType = keyBy(rowsWithPerNav, (row) => row.modelType);

    const bondDuration = byModelType.Bond?.yieldDeltaPerNav ?? null;
    const swapDuration = byModelType.Swap?.yieldDeltaPerNav ?? null;
    const swaptionDuration = byModelType.Swaption?.yieldDeltaPerNav ?? null;

    const cdsCredit =
        (byModelType.CdsIndex?.creditDeltaPerNav ?? 0) + (byModelType.CdsBasket?.creditDeltaPerNav ?? 0);
    let cdsDuration: number | null = cdsCredit;
    if (!byModelType.CdsIndex && !byModelType.CdsBasket) {
        cdsDuration = null;
    }

    return {
        Bond: bondDuration,
        Swaps: swapDuration,
        Swaption: swaptionDuration,
        CDS: cdsDuration,
        Other: null
    };
}

export function sumNavAfterAdjustmentsAfterOrders(
    classes: { navAfterAdjustmentsAfterOrders?: string; fxRate?: string }[] | null | undefined
): number | null {
    if (!classes || classes.length === 0) {
        return null;
    }
    const total = sumBy(classes, (fundClass) => Number(fundClass.navAfterAdjustmentsAfterOrders) * Number(fundClass.fxRate));
    return Number.isFinite(total) ? total : null;
}

export function sumNavBeforeAdjustmentsBeforeOrders(
    classes: { navBeforeAdjustmentsBeforeOrders?: string; fxRate?: string }[] | null | undefined
): number | null {
    if (!classes || classes.length === 0) {
        return null;
    }
    const total = sumBy(classes, (fundClass) => Number(fundClass.navBeforeAdjustmentsBeforeOrders) * Number(fundClass.fxRate));
    return Number.isFinite(total) ? total : null;
}

export function getNavDailyPerformance(
    classes:
        | {
              navPerUnitReturn1D?: string;
              navAfterAdjustmentsAfterOrders?: string;
              fxRate?: string;
          }[]
        | null
        | undefined
): number | null {
    if (!classes || classes.length === 0) {
        return null;
    }

    let weightedSum = 0;
    let totalNav = 0;
    classes.forEach((fundClass) => {
        const dailyReturn = Number(fundClass.navPerUnitReturn1D);
        const nav = Number(fundClass.navAfterAdjustmentsAfterOrders) * Number(fundClass.fxRate);
        if (!Number.isFinite(dailyReturn) || !Number.isFinite(nav) || nav === 0) {
            return;
        }
        weightedSum += dailyReturn * nav;
        totalNav += nav;
    });

    if (totalNav !== 0) {
        return weightedSum / totalNav;
    }

    const firstReturn = Number(classes[0].navPerUnitReturn1D);
    return Number.isFinite(firstReturn) ? firstReturn : null;
}

export function getTimeToMaturityYears(maturityDate: string | null | undefined, asOfDate: string): number | null {
    if (!maturityDate) {
        return null;
    }

    const years = DateHelper.yearFrac(DateHelper.toDate(asOfDate), DateHelper.toDate(maturityDate));
    return Number.isFinite(years) ? years : null;
}

export function formatTimeToMaturityYears(years: number | null): string {
    if (years === null) {
        return "-";
    }
    return years.toFixed(2);
}

function parseFormatNumericInput(d: FormatNumericInput): number {
    if (typeof d === "number") {
        return d;
    }
    if (typeof d === "string" && d !== "") {
        return Number(normalizeFoNavDecimalString(d));
    }
    return NaN;
}

export function normalizeFoNavDecimalString(value: string): string {
    return value.replace(/\s/g, "").replace(",", ".");
}

export function foNavNumberFormat(format: string) {
    return (value: FormatNumericInput): string | null => {
        const numeric = parseFormatNumericInput(value);
        if (Number.isNaN(numeric)) {
            return null;
        }
        return numberFormat(numeric, format, ".", " ").replace(",", ".");
    };
}

export function parseFoNavNumericInput(value: unknown): number | null {
    if (value === null || typeof value === "undefined" || value === "") {
        return null;
    }
    const normalized = typeof value === "string" ? normalizeFoNavDecimalString(value) : value;
    const numeric = typeof normalized === "number" ? normalized : Number(normalized);
    return Number.isFinite(numeric) ? numeric : null;
}

export function calculateRiskImpact(duration: number | null, changeBp: number | null): number | null {
    if (duration === null || changeBp === null) {
        return null;
    }
    return (-duration * changeBp) / 10000;
}

export function calculateTotalRiskImpact(impacts: Array<number | null>): number {
    return sumBy(
        impacts.filter((impact): impact is number => impact !== null),
        (impact) => impact
    );
}

export function calculateEstimatedNav(totalImpact: number, nav: number | null): number | null {
    if (nav === null) {
        return null;
    }
    return (totalImpact + 1) * nav;
}

function toFiniteNumber(value: unknown): number | null {
    if (value === null || typeof value === "undefined") {
        return null;
    }
    const numeric = typeof value === "number" ? value : Number(value);
    return Number.isFinite(numeric) ? numeric : null;
}

export function rateToPercent(value: number | null): number | null {
    if (value === null || typeof value === "undefined" || Number.isNaN(value)) {
        return null;
    }
    return value * 100;
}

export function spreadToBp(value: number | string | null | undefined): number | null {
    const numeric = toFiniteNumber(value);
    if (numeric === null) {
        return null;
    }
    return numeric * 10000;
}

export function computeChangeBp(start: unknown, end: unknown): number | null {
    const startNumeric = toFiniteNumber(start);
    const endNumeric = toFiniteNumber(end);
    if (startNumeric === null || endNumeric === null) {
        return null;
    }
    return (endNumeric - startNumeric) * 10000;
}

export function toPercentGridValue(rate: unknown): string {
    const numeric = toFiniteNumber(rate);
    if (numeric === null) {
        return "";
    }
    return (numeric * 100).toFixed(2);
}

export function toBpGridValue(rate: unknown): string {
    const numeric = toFiniteNumber(rate);
    if (numeric === null) {
        return "";
    }
    return (numeric * 10000).toFixed(1);
}

export function toYieldPercentGridValue(value: number | null | undefined): string {
    const numeric = toFiniteNumber(value);
    if (numeric === null) {
        return "";
    }
    return numeric.toFixed(2);
}

export function computeYieldPercentChangeBp(start: unknown, end: unknown): number | null {
    const startNumeric = toFiniteNumber(start);
    const endNumeric = toFiniteNumber(end);
    if (startNumeric === null || endNumeric === null) {
        return null;
    }
    return (endNumeric - startNumeric) * 100;
}

export function toYieldPercentChangeBpGridValue(start: unknown, end: unknown): string {
    const change = computeYieldPercentChangeBp(start, end);
    if (change === null) {
        return "";
    }
    return change.toFixed(1);
}

export function toBpChangeGridValue(start: unknown, end: unknown): string {
    const change = computeChangeBp(start, end);
    if (change === null) {
        return "";
    }
    return change.toFixed(1);
}

export interface FoNavSebRateInput {
    maturity: string;
    maturityYears?: number | null;
    rate?: number | null;
    change?: number | null;
    time?: string | null;
    date?: string | null;
}

export interface FoNavInternalRateInput {
    maturityYears: number | null;
    ratePercent: number | null;
}

export interface FoNavSebComparisonRow {
    tenor: string;
    sebRate: string;
    sebChange: string;
    internalRate: string;
    diff: string;
}

/**
 * Keys a tenor by whole months so that year and month labels from either source line up,
 * for example a 2Y curve instrument and SEB's "2 Yr" both land on 24.
 */
function tenorMonthsKey(years: number | null | undefined): number | null {
    if (years === null || typeof years === "undefined" || !Number.isFinite(years)) {
        return null;
    }
    return Math.round(years * 12);
}

/**
 * Lines SEB's published swap rates up against our own curve tenor by tenor. Driven off the SEB
 * rows, since the point is to check our marks against theirs rather than the reverse.
 */
export function buildFoNavSebSwapComparisonRows(
    sebRates: FoNavSebRateInput[] | null | undefined,
    internalRates: FoNavInternalRateInput[] | null | undefined
): FoNavSebComparisonRow[] {
    if (!sebRates || sebRates.length === 0) {
        return [];
    }

    const internalByTenor = new Map<number, number | null>();
    (internalRates ?? []).forEach((row) => {
        const key = tenorMonthsKey(row.maturityYears);
        if (key !== null && !internalByTenor.has(key)) {
            internalByTenor.set(key, row.ratePercent);
        }
    });

    const rows = sebRates.map((sebRate) => {
        const key = tenorMonthsKey(sebRate.maturityYears);
        let internalPercent: number | null = null;
        if (key !== null && internalByTenor.has(key)) {
            internalPercent = internalByTenor.get(key) ?? null;
        }

        const sebPercent = toFiniteNumber(sebRate.rate);

        let diff = "";
        if (internalPercent !== null && sebPercent !== null) {
            diff = ((internalPercent - sebPercent) * 100).toFixed(1);
        }

        let sebChange = "";
        const change = toFiniteNumber(sebRate.change);
        if (change !== null) {
            sebChange = (change * 100).toFixed(1);
        }

        return {
            tenor: sebRate.maturity,
            maturityYears: sebRate.maturityYears ?? null,
            sebRate: sebPercent === null ? "" : sebPercent.toFixed(2),
            sebChange,
            internalRate: internalPercent === null ? "" : internalPercent.toFixed(2),
            diff
        };
    });

    return sortBy(rows, (row) => row.maturityYears ?? Number.MAX_SAFE_INTEGER).map(
        ({ maturityYears: _maturityYears, ...row }) => row
    );
}

/**
 * Substitutes SEB's pasted levels for the end of the period while leaving the start of the period on
 * our own curve, so the resulting change is our start against their end.
 *
 * A tenor SEB does not quote is left with no end rate rather than quietly falling back to the
 * internal curve. Otherwise a gap in the paste would read as a genuine zero move and understate the
 * impact, which is the opposite of what a cross check should do.
 */
export function applyFoNavSebSwapRates(
    swaps: FoNavSwapRiskInput[] | null | undefined,
    sebRates: FoNavSebRateInput[] | null | undefined
): FoNavSwapRiskInput[] {
    if (!swaps || swaps.length === 0) {
        return [];
    }

    const sebByTenor = new Map<number, number | null>();
    (sebRates ?? []).forEach((row) => {
        const key = tenorMonthsKey(row.maturityYears);
        if (key !== null && !sebByTenor.has(key)) {
            sebByTenor.set(key, toFiniteNumber(row.rate));
        }
    });

    return swaps.map((swap) => {
        let years: number | null = null;
        if (swap.maturityPeriod) {
            years = maturityPeriodToYears(swap.maturityPeriod);
        }
        if (years === null) {
            years = toFiniteNumber(swap.timeToMaturityYears);
        }

        const key = tenorMonthsKey(years);
        let endRate: number | null = null;
        if (key !== null) {
            const percent = sebByTenor.get(key);
            if (typeof percent !== "undefined" && percent !== null) {
                // Pasted levels are percent, our curve stores fractions.
                endRate = percent / 100;
            }
        }

        return { ...swap, endRate };
    });
}

export function formatFoNavSebAsOf(rows: { time?: string | null; date?: string | null }[] | null | undefined): string | null {
    const stamped = (rows ?? []).find((row) => row.date || row.time);
    if (!stamped) {
        return null;
    }
    return [stamped.date, stamped.time].filter((part) => Boolean(part)).join(" ");
}

export function toFxGridValue(value: unknown): string {
    const numeric = toFiniteNumber(value);
    if (numeric === null) {
        return "";
    }
    return numeric.toFixed(4);
}

export function computeFxPercentChange(start: unknown, end: unknown): number | null {
    const startNumeric = toFiniteNumber(start);
    const endNumeric = toFiniteNumber(end);
    if (startNumeric === null || endNumeric === null || startNumeric === 0) {
        return null;
    }
    return ((endNumeric - startNumeric) / startNumeric) * 100;
}

export function toFxPercentChangeGridValue(start: unknown, end: unknown): string {
    const change = computeFxPercentChange(start, end);
    if (change === null) {
        return "";
    }
    return change.toFixed(2);
}

export function formatPercent(value: unknown): string {
    const numeric = toFiniteNumber(value);
    if (numeric === null) {
        return "-";
    }
    return numeric.toFixed(2);
}

export function formatBp(value: unknown): string {
    const numeric = toFiniteNumber(value);
    if (numeric === null) {
        return "-";
    }
    return numeric.toFixed(1);
}

export function getTimeseriesValue(values: string[] | null | undefined): number | null {
    if (!values || values.length === 0) {
        return null;
    }
    const parsedValue = parseFloat(values[0]);
    return Number.isFinite(parsedValue) ? parsedValue : null;
}

export interface FoNavCalendarEvent {
    date: string;
    state?: CalendarStateEnum | null;
}

/**
 * A date is a settlement day unless it falls on a weekend or the calendar marks it Closed.
 * EarlyClose days still settle, matching the backend calendar resolver.
 */
export function buildFoNavBusinessDayCheck(
    calendarEvents: FoNavCalendarEvent[] | null | undefined
): (date: string) => boolean {
    const closedDates = new Set(
        (calendarEvents ?? []).filter((event) => event.state === CalendarStateEnum.Closed).map((event) => event.date)
    );

    return (date: string): boolean => {
        const dayOfWeek = DateHelper.getDayOfWeek(date);
        if (dayOfWeek === 0 || dayOfWeek === 6) {
            return false;
        }
        return !closedDates.has(date);
    };
}

// Settlement never rolls further than a long holiday sequence, so this only guards against broken calendar data.
const MAX_SETTLEMENT_ROLL_DAYS = 40;

/**
 * Moves a nav date forward by a number of business days. A nav date that is not itself a business day
 * rolls forward first, so a zero settlement lag still lands on a day that can settle.
 */
export function addFoNavSettlementDays(
    date: string | null | undefined,
    settleDays: number,
    isBusinessDay: (date: string) => boolean
): string | null {
    if (!date) {
        return null;
    }

    let settlementDate = date;
    let steps = 0;

    while (!isBusinessDay(settlementDate) && steps < MAX_SETTLEMENT_ROLL_DAYS) {
        settlementDate = DateHelper.addDays(settlementDate, 1);
        steps++;
    }

    let daysAdded = 0;
    while (daysAdded < settleDays && steps < MAX_SETTLEMENT_ROLL_DAYS) {
        settlementDate = DateHelper.addDays(settlementDate, 1);
        steps++;
        if (isBusinessDay(settlementDate)) {
            daysAdded++;
        }
    }

    if (daysAdded < settleDays || !isBusinessDay(settlementDate)) {
        return null;
    }

    return settlementDate;
}

export function foNavSettlementCalendarYears(dates: (string | null | undefined)[]): string[] {
    const years = new Set<string>();

    dates.forEach((date) => {
        if (!date) {
            return;
        }
        const year = DateHelper.getYear(date);
        if (!Number.isFinite(year)) {
            return;
        }
        // Settlement only moves forward, so a roll over year end can reach the following year.
        years.add(String(year));
        years.add(String(year + 1));
    });

    return sortBy(Array.from(years));
}

export function foNavCalendarDayCount(startDate: string | null, endDate: string | null): number | null {
    if (!startDate || !endDate) {
        return null;
    }

    const start = DateHelper.toDate(startDate).getTime();
    const end = DateHelper.toDate(endDate).getTime();
    if (!Number.isFinite(start) || !Number.isFinite(end)) {
        return null;
    }

    return Math.round((end - start) / msPerDay);
}

export interface FoNavCarryComponent {
    weight: number;
    rate: number | null;
}

export interface FoNavCarryRate {
    rate: number | null;
    weight: number;
}

/**
 * Collapses the instruments of one asset class into a single weighted average rate plus the
 * weight that rate is exposed at, so carry can be expressed as a share of NAV.
 */
export function aggregateFoNavCarryRate(components: FoNavCarryComponent[]): FoNavCarryRate {
    const usable = components.filter(
        (component): component is FoNavCarryComponent & { rate: number } =>
            component.rate !== null && Number.isFinite(component.weight)
    );

    const weight = sumBy(usable, (component) => component.weight);
    if (usable.length === 0 || weight === 0) {
        return { rate: null, weight: 0 };
    }

    return {
        rate: sumBy(usable, (component) => component.weight * component.rate) / weight,
        weight
    };
}

export function calculateFoNavCarry(days: number | null, rate: number | null, weight: number | null): number | null {
    if (days === null || rate === null || weight === null) {
        return null;
    }
    return (weight * days * rate) / FONAV_CARRY_DAY_COUNT_BASIS;
}

/**
 * Swaps only accrue the fixed rate over their floating leg, so the floating rate is netted off the
 * weighted average. An offset that is required but could not be loaded leaves the rate undefined.
 */
export function applyFoNavCarryRateOffset(rate: number | null, offset: number | null | undefined): number | null {
    if (typeof offset === "undefined") {
        return rate;
    }
    if (rate === null || offset === null) {
        return null;
    }
    return rate - offset;
}

/**
 * Carry takes the sign of the position duration, so a position that pays the fixed rate accrues
 * negative carry. A duration that is required but could not be loaded leaves the carry undefined.
 */
export function applyFoNavCarrySign(carry: number | null, duration: number | null | undefined): number | null {
    if (typeof duration === "undefined") {
        return carry;
    }
    if (carry === null || duration === null) {
        return null;
    }
    return Math.sign(duration) * carry;
}

export function buildFoNavBondCarryComponents(
    bonds: FoNavBondRiskInput[],
    bondWeights: Record<string, number>
): FoNavCarryComponent[] {
    return bonds
        .map((bond) => {
            const weight = getFoNavBondWeight(bond.name, bondWeights);
            if (weight === null) {
                return null;
            }

            // Bond yields arrive in percent, unlike swap and CDS levels which are already fractions.
            const yieldPercent = toFiniteNumber(bond.yieldStart);
            let rate: number | null = null;
            if (yieldPercent !== null) {
                rate = yieldPercent / 100;
            }

            return { weight, rate };
        })
        .filter((component): component is FoNavCarryComponent => component !== null);
}

export function buildFoNavSwapCarryComponents(
    swaps: FoNavSwapRiskInput[],
    swapTenorWeights: Record<number, number>
): FoNavCarryComponent[] {
    return swaps
        .map((swap) => {
            let tenor: number | null = null;
            if (swap.timeToMaturityYears !== null) {
                tenor = Math.round(swap.timeToMaturityYears);
            }

            let weight: number | null = null;
            if (tenor !== null) {
                weight = swapTenorWeights[tenor] ?? null;
            }
            if (weight === null) {
                return null;
            }

            return { weight, rate: toFiniteNumber(swap.startRate) };
        })
        .filter((component): component is FoNavCarryComponent => component !== null);
}

export function buildFoNavCdsCarryComponents(
    cdsRows: FoNavCdsRiskInput[],
    cdsWeights: Record<string, number>
): FoNavCarryComponent[] {
    return cdsRows
        .map((row) => {
            const weight = cdsWeights[row.name] ?? null;
            if (weight === null || weight === 0) {
                return null;
            }
            return { weight, rate: toFiniteNumber(row.startPrice) };
        })
        .filter((component): component is FoNavCarryComponent => component !== null);
}
